import React, { useEffect, useState } from "react";
import api from '../api';

// Import tab components
import { StatsTab } from '../components/admin/Stats';
import { UsersTab, AddUserForm, EditUserForm } from '../components/admin/Users';
import { DecksTab, AddDeckForm, EditDeckForm } from '../components/admin/Decks';
import { SymbolsTab, AddSymbolForm, EditSymbolForm } from '../components/admin/Symbols';
import { LanguagesTab } from '../components/admin/Languages';
import { CardTypesTab, AddCardTypeForm, EditCardTypeForm } from '../components/admin/CardTypes';
import AdminSidePanel from '../components/admin/AdminSidePanel';
import ResizablePanels from '../components/ResizablePanels';
import { CardsTab, AddCardForm, EditCardForm } from '../components/admin/Cards';
import { ReportsTab } from '../components/admin/Reports';

const TabContent = ({ active, children }) => (
  <div style={{ display: active ? "block" : "none" }}>
    {children}
  </div>
);

const Admin = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [cardTypes, setCardTypes] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [users, setUsers] = useState([]);
  const [decks, setDecks] = useState([]);
  const [stats, setStats] = useState(null);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [cards, setCards] = useState([]);
  const [reports, setReports] = useState([]);
  const [showAddCardForm, setShowAddCardForm] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  // Clear selected item when tab changes
  useEffect(() => {
    setSelectedItem(null);
    setItemDetails(null);
    setEditingItem(null);
    setEditingCard(null);
    setShowAddForm(false);
    setShowAddCardForm(false);
  }, [activeTab]);

  const handleRowClick = async (tab, item) => {
    if (selectedItem?.id === item.id) {
      setSelectedItem(null);
      setItemDetails(null);
      return;
    }
    setSelectedItem({ tab, id: item.id || item.user_id || item.type_id || item.deck_id || item.card_id || item.symbol_id || item.report_id });
    setLoadingDetails(true);
    setItemDetails(null);
    
    try {
      let details;
      switch (tab) {
        case 'users':
          details = users.find(u => u.user_id === item.user_id);
          break;
        case 'symbols':
          details = symbols.find(s => s.symbol_id === item.symbol_id);
          break;
        case 'cardTypes':
          details = cardTypes.find(c => c.type_id === item.type_id);
          break;
        case 'decks':
          details = decks.find(d => d.deck_id === item.deck_id);
          break;
        case 'cards':
          details = cards.find(c => c.card_id === item.card_id);
          break;
        case 'reports':
          details = reports.find(r => r.report_id === item.report_id);
          break;
        default:
          details = item;
      }
      setItemDetails(details);
    } catch (err) {
      console.error('Error fetching details:', err);
      setItemDetails(item);
    }
    setLoadingDetails(false);
  };

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const [cardTypesRes, symbolsRes, usersRes, decksRes, statsRes, languagesRes, cardsRes, reportsRes] = await Promise.all([
        api.get("/api/admin/card-types"),
        api.get("/api/admin/symbols"),
        api.get("/api/admin/users"),
        api.get("/api/admin/decks"),
        api.get("/api/admin/stats"),
        api.get("/api/admin/languages"),
        api.get("/api/admin/cards"),
        api.get("/api/admin/reports")
      ]);
      setCardTypes(cardTypesRes.data);
      setSymbols(symbolsRes.data);
      setUsers(usersRes.data);
      setDecks(decksRes.data);
      setStats(statsRes.data);
      setLanguages(languagesRes.data);
      setCards(cardsRes.data);
      setReports(reportsRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(err?.response?.data?.error || err.message || "Failed to load admin lists");
    }
    setLoading(false);
  };

  const handleAdd = async (type, data) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      let response;
      switch (type) {
        case "cardTypes":
          response = await api.post("/api/admin/card-types", data);
          break;
        case "symbols":
          response = await api.post("/api/admin/symbols", data);
          setSymbols(prevSymbols => [...prevSymbols, response.data]);
          break;
        case "decks":
          response = await api.post("/api/admin/decks", { ...data, owner_id: 1 });
          break;
        case "users":
          response = await api.post("/api/admin/users", data);
          setUsers(prevUsers => [...prevUsers, response.data]);
          break;
        default:
          throw new Error("Invalid type");
      }
      setSuccess(`${type.slice(0, -1)} added successfully`);
      if (type !== "symbols") {
        fetchData();
      }
      setShowAddForm(false);
    } catch (err) {
      setError(`Failed to add ${type.slice(0, -1)}: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleEdit = async (type, id, data) => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      let response;
      switch (type) {
        case "cardTypes":
          response = await api.put(`/api/admin/card-types/${id}`, data);
          break;
        case "symbols":
          response = await api.put(`/api/admin/symbols/${id}`, data);
          break;
        case "users":
          response = await api.put(`/api/admin/users/${id}`, data);
          break;
        case "decks":
          response = await api.put(`/api/admin/decks/${id}`, data);
          break;
        default:
          throw new Error("Invalid type");
      }
      setSuccess(`${type.slice(0, -1)} updated successfully`);
      fetchData(); // Refresh data
      setEditingItem(null);
    } catch (err) {
      setError(`Failed to update ${type.slice(0, -1)}: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleDelete = async (type, id) => {
    const typeName = type.slice(0, -1);
    if (!window.confirm(`Are you sure you want to delete this ${typeName}?`)) {
      return;
    }
    try {
      setLoading(true);
      setError("");
      setSuccess("");
      switch (type) {
        case "cardTypes":
          await api.delete(`/api/admin/card-types/${id}`);
          break;
        case "symbols":
          await api.delete(`/api/admin/symbols/${id}`);
          break;
        case "decks":
          await api.delete(`/api/admin/decks/${id}`);
          break;
        case "users":
          await api.delete(`/api/admin/users/${id}`);
          break;
        default:
          throw new Error("Invalid type");
      }
      setSuccess(`${typeName} deleted successfully`);
      fetchData(); // Refresh data
    } catch (err) {
      setError(`Failed to delete ${typeName}: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleAddLanguage = async ({ code, name }) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      // Step 1: Create the language entry
      setSuccess("Adding language...");
      await api.post("/api/admin/languages", { code, name });

      if (code !== 'en') {
        // Step 2: Translate symbols
        setSuccess("Translating symbols...");
        await api.post(`/api/admin/languages/${code}/translate/symbols`);

        // Step 3: Translate card types
        setSuccess("Translating card types...");
        await api.post(`/api/admin/languages/${code}/translate/card-types`);

        // Step 4: Translate decks
        setSuccess("Translating decks...");
        await api.post(`/api/admin/languages/${code}/translate/decks`);

        // Step 5: Translate cards (longest step)
        setSuccess("Translating cards — this may take a few minutes...");
        await api.post(`/api/admin/languages/${code}/translate/cards`);
      }

      setSuccess("Language and all translations added successfully!");
      fetchData();
    } catch (err) {
      setError(`Failed to add language: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleDeleteLanguage = async (code) => {
    if (!window.confirm(`Are you sure you want to delete language '${code}'?`)) return;
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.delete(`/api/admin/languages/${code}`);
      setSuccess("Language deleted successfully");
      fetchData();
    } catch (err) {
      setError(`Failed to delete language: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleAddCard = async (data) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.post('/api/admin/cards', data);
      setSuccess('Card added successfully');
      setShowAddCardForm(false);
      fetchData();
    } catch (err) {
      setError(`Failed to add card: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleEditCard = async (cardId, data, isDelete = false) => {
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      if (isDelete) {
        await api.delete(`/api/admin/cards/${cardId}`);
        setSuccess('Card deleted successfully');
      } else {
        await api.put(`/api/admin/cards/${cardId}`, data);
        setSuccess('Card updated successfully');
      }
      setEditingCard(null);
      fetchData();
    } catch (err) {
      setError(`Failed to update card: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleReportAction = async (report) => {
    const newStatus = report.status === 'open' ? 'closed' : 'open';
    
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      await api.put(`/api/admin/reports/${report.report_id}`, {
        status: newStatus,
        feedback: ''
      });
      setSuccess(`Report ${newStatus === 'closed' ? 'closed' : 'reopened'} successfully`);
      fetchData();
    } catch (err) {
      setError(`Failed to update report: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleEditReportCard = async (report) => {
    // Fetch full card details and open edit form
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.get(`/api/admin/cards/${report.card_id}`);
      setShowAddForm(false);
      setEditingCard(res.data);
    } catch (err) {
      setError(`Failed to load card details: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleEditReportUser = async (report) => {
    // Fetch full user details and open edit form
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const res = await api.get(`/api/admin/users/${report.reported_by}`);
      setShowAddForm(false);
      setEditingItem({ type: "users", ...res.data });
    } catch (err) {
      setError(`Failed to load user details: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const handleEditReportDeck = async (report) => {
    // Find full deck details from the decks list and open edit form
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const deck = decks.find(d => d.deck_id === report.deck_id);
      if (!deck) {
        setError(`Deck with ID ${report.deck_id} not found`);
        setLoading(false);
        return;
      }
      setShowAddForm(false);
      setEditingItem({ type: "decks", ...deck });
    } catch (err) {
      setError(`Failed to load deck details: ${err?.response?.data?.error || err.message}`);
    }
    setLoading(false);
  };

  const renderAddForm = () => {
    const formProps = {
      onCancel: () => {
        setShowAddForm(false);
        setShowAddCardForm(false);
      },
      languages: languages,
    }
    switch (showAddForm) {
      case "cardTypes":
        return <AddCardTypeForm {...formProps} onAdd={(data) => handleAdd("cardTypes", data)} symbols={symbols} />;
      case "symbols":
        return <AddSymbolForm {...formProps} onAdd={(data) => handleAdd("symbols", data)} />;
      case "decks":
        return <AddDeckForm {...formProps} onAdd={(data) => handleAdd("decks", data)} users={users} decks={decks} symbols={symbols} />;
      case "users":
        return <AddUserForm {...formProps} onAdd={(data) => handleAdd("users", data)} />;
      default:
        break;
    }
    if (showAddCardForm) {
      return <AddCardForm decks={decks} cardTypes={cardTypes} onAdd={handleAddCard} onCancel={() => setShowAddCardForm(false)} languages={languages} symbols={symbols} />;
    }
    return null;
  };

  const renderEditForm = () => {
    if (!editingItem && !editingCard) return null;
    const formProps = {
      onCancel: () => {
        setEditingItem(null);
        setEditingCard(null);
      },
      languages: languages,
    };
    if (editingItem) {
      switch (editingItem.type) {
        case "decks":
          return <EditDeckForm {...formProps} deck={editingItem} onSave={(data) => handleEdit("decks", editingItem.deck_id, data)} users={users} decks={decks} symbols={symbols} />;
        case "symbols":
          return <EditSymbolForm {...formProps} symbol={editingItem} onSave={(data) => handleEdit("symbols", editingItem.symbol_id, data)} />;
        case "cardTypes":
          return <EditCardTypeForm {...formProps} cardType={editingItem} onSave={(data) => handleEdit("cardTypes", editingItem.type_id, data)} symbols={symbols} />;
        case "users":
          return <EditUserForm {...formProps} user={editingItem} onSave={(data) => handleEdit("users", editingItem.user_id, data)} />;
        default:
          return null;
      }
    }
    if (editingCard) {
      return <EditCardForm card={editingCard} decks={decks} cardTypes={cardTypes} onSave={handleEditCard} onCancel={() => setEditingCard(null)} languages={languages} symbols={symbols} />;
    }
    return null;
  };

  return (
    <div className="admin-page" style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: 0 }}>
      <ResizablePanels
        left={
          <div style={{ height: '100%', overflow: 'auto', padding: '20px' }}>
            <button className="admin-nav-btn" onClick={() => setActiveTab("overview")}>Overview</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("users")}>Users</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("languages")}>Languages</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("symbols")}>Symbols</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("cardTypes")}>Card Types</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("decks")}>Decks</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("cards")}>Cards</button><br /><br />
            <button className="admin-nav-btn" onClick={() => setActiveTab("reports")}>Reports</button>
          </div>
        }
        center={
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ padding: '20px 20px 0 20px' }}>
              {loading && <p>Loading...</p>}
              {success && <p style={{ color: "green" }}>{success}</p>}
              {error && <p style={{ color: "red" }}>{error}</p>}
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: '0 20px 20px 20px', minHeight: 0 }}>
              <TabContent active={activeTab === "overview"}>
                <StatsTab stats={stats} />
              </TabContent>
              <TabContent active={activeTab === "users"}>
                <UsersTab users={users} onEditUser={(user) => { setShowAddForm(false); setEditingItem({ type: "users", ...user }); }} onDeleteUser={(userId) => handleDelete("users", userId)} languages={languages} onRowClick={handleRowClick} />
              </TabContent>
              <TabContent active={activeTab === "languages"}>
                <LanguagesTab languages={languages} onAddLanguage={handleAddLanguage} onDeleteLanguage={handleDeleteLanguage} loading={loading} onRowClick={handleRowClick} />
              </TabContent>
              <TabContent active={activeTab === "symbols"}>
                <SymbolsTab symbols={symbols} onEditSymbol={(symbol) => { setShowAddForm(false); setEditingItem({ type: "symbols", ...symbol }); }} onDeleteSymbol={(symbolId) => handleDelete("symbols", symbolId)} onRowClick={handleRowClick} />
              </TabContent>
              <TabContent active={activeTab === "cardTypes"}>
                <CardTypesTab cardTypes={cardTypes} onEditCardType={(item) => { setShowAddForm(false); setEditingItem({ type: 'cardTypes', ...item }); }} onDeleteCardType={(id) => handleDelete("cardTypes", id)} onRowClick={handleRowClick} />
              </TabContent>
              <TabContent active={activeTab === "decks"}>
                <DecksTab decks={decks} users={users} onEditDeck={(deck) => { setShowAddForm(false); setEditingItem({ type: "decks", ...deck }); }} onDeleteDeck={(deckId) => handleDelete("decks", deckId)} onRowClick={handleRowClick} />
              </TabContent>
              <TabContent active={activeTab === "cards"}>
                <CardsTab
                  cards={cards}
                  decks={decks}
                  cardTypes={cardTypes}
                  users={users}
                  languages={languages}
                  onAddCard={handleAddCard}
                  onEditCard={handleEditCard}
                  showAddForm={showAddCardForm}
                  setShowAddForm={setShowAddCardForm}
                  editingCard={editingCard}
                  setEditingCard={setEditingCard}
                  onImportComplete={fetchData}
                  onRowClick={handleRowClick}
                />
              </TabContent>
              <TabContent active={activeTab === "reports"}>
                <ReportsTab
                  reports={reports}
                  decks={decks}
                  cardTypes={cardTypes}
                  users={users}
                  languages={languages}
                  symbols={symbols}
                  onEditReport={handleReportAction}
                  onEditCard={handleEditReportCard}
                  onEditDeck={handleEditReportDeck}
                  onEditUser={handleEditReportUser}
                  onRowClick={handleRowClick}
                />
              </TabContent>
            </div>
          </div>
        }
        right={
          <div style={{ height: '100%', overflow: 'auto', padding: '20px' }}>
            <AdminSidePanel
              activeTab={activeTab}
              showAddForm={showAddForm}
              setShowAddForm={setShowAddForm}
              editingItem={editingItem}
              renderAddForm={renderAddForm}
              renderEditForm={renderEditForm}
              showAddCardForm={showAddCardForm}
              setShowAddCardForm={setShowAddCardForm}
              editingCard={editingCard}
              setEditingCard={setEditingCard}
              selectedItem={selectedItem}
              setSelectedItem={setSelectedItem}
              itemDetails={itemDetails}
              loadingDetails={loadingDetails}
              onEditReportCard={handleEditReportCard}
              onEditReportDeck={handleEditReportDeck}
              onEditReportUser={handleEditReportUser}
            />
          </div>
        }
      />
    </div>
  );
};

export default Admin;